import {
	createActionsHook,
	createStateHook,
	createStore,
	type StoreActionApi,
} from 'react-sweet-state';

/**
 * Shared store for the third-party connector setup modal.
 *
 * The modal open state is held in a module-level react-sweet-state store so it
 * is decoupled from where the modal is rendered. Multiple surfaces (sidebar,
 * nav search) can call `openSetupModal` to request the modal, while a single
 * always-mounted host (ConnectorSetupModalHost) reads `activeConnectorKey` and
 * renders the modal. This avoids the modal being torn down when the surface
 * that triggered it unmounts (e.g. the QuickFind search dialog closing).
 */
type SetupConnectorState = {
	activeConnectorKey: string | null;
};

const initialState: SetupConnectorState = {
	activeConnectorKey: null,
};

const actions = {
	openSetupModal:
		(connectorKey: string) =>
		({ setState }: StoreActionApi<SetupConnectorState>): void => {
			setState({ activeConnectorKey: connectorKey });
		},
	closeModal:
		() =>
		({ setState }: StoreActionApi<SetupConnectorState>): void => {
			setState({ activeConnectorKey: null });
		},
};

const SetupConnectorStore = createStore({
	initialState,
	actions,
	name: 'Twcg1489SetupConnectorStore',
});

/**
 * Actions-only hook (`openSetupModal`, `closeModal`) for the setup-connector
 * store. The returned actions are stable references, so consuming this instead
 * of `useSetupConnector` avoids subscribing to `activeConnectorKey`. Use this in
 * components that only trigger the modal (e.g. the surfaces via
 * `useConnectorPromoConfig`) so they do not re-render on every modal open/close.
 */
export const useSetupConnectorActions = createActionsHook(SetupConnectorStore);

const useActiveConnectorKey = createStateHook(SetupConnectorStore, {
	selector: (state: SetupConnectorState) => state.activeConnectorKey,
});

/**
 * Hook to manage the third-party connector setup modal state.
 *
 * Returns `openSetupModal` to set the active connector key,
 * `activeConnectorKey` to determine which connector modal to show,
 * and `closeModal` to dismiss it. State is shared across all callers via a
 * module-level store, so the modal can be opened from one surface and rendered
 * by a separate always-mounted host (ConnectorSetupModalHost).
 *
 * This subscribes to `activeConnectorKey`; components that only need to trigger
 * the modal should use `useSetupConnectorActions` to avoid re-rendering on every
 * modal open/close.
 */
export function useSetupConnector(): {
	openSetupModal: (connectorKey: string) => void;
	activeConnectorKey: string | null;
	closeModal: () => void;
} {
	const activeConnectorKey = useActiveConnectorKey();
	const { openSetupModal, closeModal } = useSetupConnectorActions();

	return { openSetupModal, activeConnectorKey, closeModal };
}
